import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Car, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguagePicker from '../components/LanguagePicker';

export default function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await forgotPassword(email);
    
    if (result.success) {
      setSent(true);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-8">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-[#16A34A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#16A34A]" />
          </div>
          <h1 
            className="text-3xl font-bold text-[#0A0A0A] mb-4 tracking-tight"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            Check your email
          </h1>
          <p className="text-[#52525B] mb-8">
            If an account exists for {email}, we've sent a password reset link.
          </p>
          <Link to="/login">
            <Button className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 mb-8" data-testid="forgot-password-logo">
          <img 
            src="/wakzy-logo.png" 
            alt="Wakzy Auto Tech" 
            className="h-16 w-auto"
          />
        </Link>

        <div className="bg-white rounded-md border border-[rgba(10,10,10,0.1)] p-8">
          <Link to="/login" className="inline-flex items-center text-[#52525B] hover:text-[#0A0A0A] mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Sign In
          </Link>

          <div className="flex items-center justify-between mb-2">
          <h1 
            className="text-3xl font-bold text-[#0A0A0A] tracking-tight"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            data-testid="forgot-password-title"
          >
            {t('auth.resetPassword')}
          </h1>
          <LanguagePicker />
          </div>
          <p className="text-[#52525B] mb-8">
            {t('auth.resetSub') || "Enter your email and we'll send you a reset link."}
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6" data-testid="forgot-password-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-[#0A0A0A] font-medium">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 border-[rgba(10,10,10,0.1)] focus:ring-[#0052FF] focus:border-[#0052FF]"
                required
                data-testid="forgot-password-email-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#0052FF] hover:bg-[#0043D1] text-white font-semibold"
              data-testid="forgot-password-submit-btn"
            >
              {loading ? t('common.loading') : t('auth.sendResetLink')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
